import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Pin, Plus, X, Trash2, Loader2, Check, Megaphone, Eye, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api } from "../../lib/api";
import SelectField from "../../components/os/SelectField";

const input = "w-full bg-white border border-navy/10 rounded-lg px-3 py-2.5 outline-none focus:border-violet text-sm";
const CATS = [{ value: "general", label: "General" }, { value: "rh", label: "Recursos Humanos" }, { value: "eventos", label: "Eventos" }, { value: "urgente", label: "Urgente" }];
const REACTIONS = [
  { key: "clap", emoji: "👏", label: "Aplausos" },
  { key: "heart", emoji: "❤️", label: "Me encanta" },
  { key: "thumb", emoji: "👍", label: "Me gusta" },
];

export default function Announcements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ title: "", body: "", pinned: false, category: "general" });
  const [commentFor, setCommentFor] = useState(null);
  const [comment, setComment] = useState("");
  const viewed = useRef(new Set());

  const load = () =>
    api.get("/px/announcements").then((r) => {
      setItems(r.data);
      r.data.forEach((a) => {
        if (!a.read && !viewed.current.has(a.id)) {
          viewed.current.add(a.id);
          api.post(`/px/announcements/${a.id}/read`).catch(() => {});
        }
      });
    }).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.title.trim()) return toast.error("El título es obligatorio");
    try { await api.post("/px/announcements", f); setOpen(false); setF({ title: "", body: "", pinned: false, category: "general" }); load(); toast.success("Comunicado publicado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const react = async (a, type) => { try { await api.post(`/px/announcements/${a.id}/react`, { type }); load(); } catch { /* */ } };
  const sendComment = async (a) => { if (!comment.trim()) return; try { await api.post(`/px/announcements/${a.id}/comment`, { text: comment }); setComment(""); setCommentFor(null); load(); } catch { /* */ } };
  const del = async (a) => { try { await api.delete(`/px/announcements/${a.id}`); load(); } catch { /* */ } };

  return (
    <OSLayout title="OMNIA Announcements">
      <div className="max-w-3xl mx-auto space-y-6" data-testid="announcements-page">
        <div className="flex items-end justify-between gap-3">
          <div>
            <div className="overline text-navy/40 mb-2">OMNIA People Suite · Human OS</div>
            <h2 className="font-editorial text-4xl tracking-tight">OMNIA Announcements</h2>
            <p className="mt-2 text-navy/50">Comunicados oficiales de la empresa. Reacciona, comenta y mide su alcance.</p>
          </div>
          <button onClick={() => setOpen(true)} className="btn btn-primary shrink-0" data-testid="ann-new"><Plus className="w-4 h-4" /> Nuevo</button>
        </div>

        {loading ? <div className="py-12 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> :
          items.length === 0 ? <div className="card-surface py-12 text-center text-navy/40 text-sm">Sin comunicados aún.</div> :
          items.map((a) => (
            <motion.div key={a.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className={`card-surface p-5 ${!a.read ? "border-l-4 border-l-violet" : ""}`} data-testid={`ann-${a.id}`}>
              <div className="flex items-center gap-2 mb-1">
                {a.pinned && <Pin className="w-3.5 h-3.5 text-violet" />}
                <span className="badge badge-violet">{CATS.find((c) => c.value === a.category)?.label || a.category}</span>
                {!a.read && <span className="badge badge-amber">Nuevo</span>}
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-navy/40" data-testid={`ann-views-${a.id}`}><Eye className="w-3.5 h-3.5" /> {a.view_count || 0}</span>
                <button onClick={() => del(a)} className="text-navy/25 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
              <h3 className="font-medium text-lg">{a.title}</h3>
              {a.body && <p className="text-sm text-navy/70 mt-1 whitespace-pre-wrap">{a.body}</p>}
              <div className="text-[11px] text-navy/40 mt-3">{a.author} · {new Date(a.created_at).toLocaleString("es-MX")}</div>

              <div className="flex items-center gap-2 mt-4 flex-wrap">
                {REACTIONS.map((r) => {
                  const count = a.reactions?.[r.key]?.length || 0;
                  return (
                    <button key={r.key} onClick={() => react(a, r.key)} title={r.label}
                      className={`inline-flex items-center gap-1.5 text-sm rounded-full px-2.5 py-1 border transition-colors ${count ? "border-violet/30 bg-violet/5 text-navy" : "border-navy/10 text-navy/50 hover:border-violet/30"}`}
                      data-testid={`ann-react-${r.key}-${a.id}`}>
                      <span className="text-base leading-none">{r.emoji}</span> {count}
                    </button>
                  );
                })}
                <button onClick={() => setCommentFor(commentFor === a.id ? null : a.id)} className="inline-flex items-center gap-1.5 text-sm text-navy/50 hover:text-violet ml-1" data-testid={`ann-comment-toggle-${a.id}`}><MessageCircle className="w-4 h-4" /> {a.comments?.length || 0}</button>
              </div>
              {a.comments?.length > 0 && <div className="mt-3 space-y-1.5 border-t border-navy/5 pt-3">{a.comments.map((c) => <div key={c.id} className="text-sm"><span className="font-medium">{c.by}: </span><span className="text-navy/70">{c.text}</span></div>)}</div>}
              {commentFor === a.id && (
                <div className="flex gap-2 mt-3">
                  <input className={input} placeholder="Escribe un comentario…" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment(a)} data-testid={`ann-comment-input-${a.id}`} />
                  <button onClick={() => sendComment(a)} className="btn btn-outline text-sm" data-testid={`ann-comment-send-${a.id}`}>Enviar</button>
                </div>
              )}
            </motion.div>
          ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="ann-dialog">
          <div className="card-surface w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4"><h3 className="font-editorial text-2xl flex items-center gap-2"><Megaphone className="w-5 h-5" /> Nuevo comunicado</h3><button onClick={() => setOpen(false)}><X className="w-5 h-5 text-navy/40" /></button></div>
            <div className="space-y-3">
              <input className={input} placeholder="Título" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="ann-title" />
              <textarea className={input} rows={4} placeholder="Mensaje" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
              <SelectField value={f.category} options={CATS} onChange={(v) => setF({ ...f, category: v })} />
              <label className="flex items-center gap-2 text-sm text-navy/70 cursor-pointer"><input type="checkbox" checked={f.pinned} onChange={(e) => setF({ ...f, pinned: e.target.checked })} /> Fijar en la parte superior</label>
            </div>
            <div className="flex justify-end gap-2 mt-5"><button onClick={() => setOpen(false)} className="btn btn-ghost">Cancelar</button><button onClick={create} className="btn btn-primary" data-testid="ann-submit"><Check className="w-4 h-4" /> Publicar</button></div>
          </div>
        </div>
      )}
    </OSLayout>
  );
}
